import { Injectable, signal } from '@angular/core';

import { runSafely } from '@helpers';

import { NEW_RULE_DRAFT_KEY, NewRuleDraftState } from './new-rules-storage';

@Injectable({ providedIn: 'root' })
export class RuleDraftService {
  readonly hasDraft = signal<boolean>(this.readHasDraft());
  readonly ruleId = signal<string | null>(this.readRuleId());

  refreshFromStorage(): void {
    const hasDraft = this.readHasDraft();
    this.hasDraft.set(hasDraft);
    this.ruleId.set(hasDraft ? this.readRuleId() : null);
  }

  clearDraft(): void {
    runSafely(() => localStorage.removeItem(NEW_RULE_DRAFT_KEY));
    this.hasDraft.set(false);
    this.ruleId.set(null);
  }

  getDraftUrl(): string[] {
    const ruleId = this.ruleId();
    return ruleId ? ['/admin/rule', ruleId] : ['/admin/new-rules'];
  }

  isDraftRoute(url: string): boolean {
    const currentPath = url.split(/[?#]/, 1)[0].replace(/\/$/, '');
    const ruleId = this.ruleId();
    const draftPath = ruleId ? `/admin/rule/${encodeURIComponent(ruleId)}` : '/admin/new-rules';
    return currentPath === draftPath;
  }

  private readHasDraft(): boolean {
    try {
      const raw = localStorage.getItem(NEW_RULE_DRAFT_KEY);
      if (!raw) return false;

      const data = JSON.parse(raw) as Partial<NewRuleDraftState> | null;
      if (!data) return false;

      const hasHeader =
        !!data.header &&
        (!!data.header.name ||
          !!data.header.enabled ||
          !!data.header.description ||
          !!(data.header.events && data.header.events.length));

      const hasMain = !!data.nodesMain && data.nodesMain.length > 0;
      const hasWrite = !!data.nodesWriteoff && data.nodesWriteoff.length > 0;

      return hasHeader || hasMain || hasWrite;
    } catch {
      return false;
    }
  }

  private readRuleId(): string | null {
    try {
      const raw = localStorage.getItem(NEW_RULE_DRAFT_KEY);
      if (!raw) return null;

      const data = JSON.parse(raw) as Partial<NewRuleDraftState> | null;
      const ruleId = String(data?.ruleId ?? '').trim();
      return ruleId || null;
    } catch {
      return null;
    }
  }
}
